import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { treatmentAPI, patientAPI } from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const PatientInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

function TreatmentForm() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [treatmentSuggestions, setTreatmentSuggestions] = useState([]);

  const [formData, setFormData] = useState({
    patientId: patientId,
    treatmentDate: dayjs(),
    treatmentType: '',
    notes: '',
    status: 'Scheduled'
  });

  useEffect(() => {
    fetchPatientDetails();
    fetchTreatmentSuggestions();

    // Pre-fill treatment type from URL parameter
    const treatmentName = searchParams.get('treatmentName');
    if (treatmentName) {
      setFormData(prev => ({
        ...prev,
        treatmentType: treatmentName
      }));
    }
  }, [patientId, searchParams]);

  const fetchPatientDetails = async () => {
    try {
      const response = await patientAPI.getById(patientId);
      setPatient(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch patient details');
    }
  };

  const fetchTreatmentSuggestions = async () => {
    try {
      const response = await treatmentAPI.getSuggestions(patientId);
      setTreatmentSuggestions(response.data.data);
    } catch (err) {
      console.error('Failed to fetch treatment suggestions');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (newValue) => {
    setFormData({
      ...formData,
      treatmentDate: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        treatmentDate: formData.treatmentDate?.toISOString()
      };
      await treatmentAPI.create(submitData);
      navigate(`/patient/${patientId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create treatment record');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (!patient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
            📋 Schedule Treatment Plan
          </Typography>

          <PatientInfoCard>
            <Typography variant="h6" gutterBottom>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Name:</strong> {patient.firstName} {patient.lastName}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography><strong>Age:</strong> {calculateAge(patient.dateOfBirth)} years</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography><strong>Gender:</strong> {patient.gender}</Typography>
              </Grid>
              {patient.allergies && (
                <Grid item xs={12}>
                  <Chip
                    label={`⚠️ Allergies: ${patient.allergies}`}
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Grid>
              )}
            </Grid>
          </PatientInfoCard>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Treatment Plan Date & Time *"
                  value={formData.treatmentDate}
                  onChange={handleDateChange}
                  sx={{ width: '100%' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleChange}
                  >
                    <MenuItem value="Scheduled">Scheduled</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={treatmentSuggestions.map(suggestion => suggestion.name)}
                  value={formData.treatmentType}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      treatmentType: newValue || ''
                    });
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({
                      ...formData,
                      treatmentType: newInputValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Treatment Plan Type *"
                      required
                      placeholder="e.g., Physiotherapy, Injection, Dressing, X-Ray"
                      helperText={treatmentSuggestions.length > 0 ?
                        `${treatmentSuggestions.length} suggested treatment plans from previous visitations` :
                        'Enter treatment plan type'
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Any additional observations or notes..."
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/patient/${patientId}`)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} />}
                  >
                    {loading ? 'Scheduling...' : 'Schedule Treatment Plan'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
}

export default TreatmentForm;